import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { AlertTriangle, LoaderCircle } from "lucide-react";
import { parsePreview, type PreviewSheet } from "@/lib/api/import";
import { listProfiles, uploadWorkbook } from "@/lib/api/endpoints";
import { peekPendingUpload, takePendingUpload } from "@/lib/import/pending-upload";
import {
  HeaderConfigurator,
  type ImportPicks,
  type ImportTemplateOption,
} from "@/components/import/HeaderConfigurator";
import type { ImportConfig } from "@/lib/mapper/types";

/** Where to land after import: straight into the mapper for a single sheet. */
export function afterImportPath(workbookId: string, importedSheets: string[]): string {
  return importedSheets.length === 1
    ? `/workbooks/${workbookId}/sheets/${encodeURIComponent(importedSheets[0])}`
    : `/workbooks/${workbookId}`;
}

export function ImportPage() {
  const navigate = useNavigate();
  // Read the pending upload WITHOUT consuming it. React Strict Mode double-mounts
  // in dev; a consuming read (takePendingUpload) would clear it on the first mount
  // and the second mount would see null and bounce back to /upload (the "flash
  // then back to upload" bug). `peek` is stable across the remount; we only clear
  // the store after a successful upload.
  const [pending] = useState(() => peekPendingUpload());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<PreviewSheet[]>([]);
  const [templates, setTemplates] = useState<ImportTemplateOption[]>([]);
  const [busy, setBusy] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!pending) {
      // Direct nav / refresh with nothing staged — go pick a file.
      navigate("/upload", { replace: true });
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);

    // Templates are only offered for the user to pick from; a failure here
    // just leaves "No template" as the only choice.
    const templatesLoad = listProfiles()
      .then((profiles) =>
        profiles
          .map((p) => ({ id: p.id, name: p.name }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      )
      .catch(() => [] as ImportTemplateOption[]);

    Promise.all([parsePreview(pending.file), templatesLoad])
      .then(([result, templateOptions]) => {
        if (controller.signal.aborted) return;
        setSheets(result.sheets);
        setTemplates(templateOptions);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load file preview");
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [pending, navigate]);

  async function handleConfirm(configs: Record<string, ImportConfig>, picks?: ImportPicks) {
    if (!pending) return;

    setBusy(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", pending.file);
      const data = await uploadWorkbook(formData, configs, picks);
      takePendingUpload(); // clear the staged file now that it's imported
      navigate(afterImportPath(data.id, data.sheets.map((sheet) => sheet.name)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1240px] px-4 py-7 lg:px-6">
      {/* Page header */}
      <header className="mb-8">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
          Workbook intake · Step 2
        </p>
        <h1 className="mt-1 text-[26px] font-semibold tracking-[-0.02em] text-slate-900 dark:text-slate-100">
          Pick your sheets, headers and template
        </h1>
        <p className="mt-2 max-w-2xl text-[14px] leading-6 text-slate-600 dark:text-slate-400">
          Tick the sheets to import, check where each sheet&apos;s header and data
          start (fix it right on the grid if needed), then choose the template to
          map it with.
        </p>
        {pending?.file && (
          <p className="mt-2 text-[12px] text-slate-400 dark:text-slate-500">
            File: <span className="font-mono">{pending.file.name}</span>
          </p>
        )}
      </header>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500 dark:text-slate-400">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Analysing file…
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-[10px] border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Failed to load preview</p>
            <p className="mt-0.5">{error}</p>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mt-2 text-[12px] underline underline-offset-2"
            >
              Go back
            </button>
          </div>
        </div>
      )}

      {/* Configurator */}
      {!loading && !error && sheets.length > 0 && (
        <div className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-sm dark:border-border dark:bg-surface sm:p-7">
          <HeaderConfigurator
            sheets={sheets}
            mode="create"
            busy={busy}
            confirmLabel={busy ? "Uploading…" : "Import"}
            templates={templates}
            onConfirm={handleConfirm}
            onCancel={() => navigate(-1)}
          />
        </div>
      )}

      {!loading && !error && sheets.length === 0 && (
        <div className="py-12 text-center text-sm text-slate-500 dark:text-slate-400">
          No sheets were found in this file.{" "}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="underline underline-offset-2"
          >
            Go back
          </button>
        </div>
      )}
    </div>
  );
}
